import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperIconComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';

/** One row of the quick-start list: a chord and what it does. */
interface CmdkShortcutHint {
  readonly keys: readonly { readonly keys: string; readonly mod: boolean; readonly label: string }[];
  readonly text: string;
}

/**
 * The preview pane with nothing selected.
 *
 * Never a blank pane. An empty right-hand column in a two-pane surface reads
 * as a loading failure, and the moment it is most likely to be seen is the
 * moment the user has just opened the palette and knows least about it — so
 * the space goes to teaching the shortcuts that are otherwise invisible.
 *
 * The design also specifies a "Right now" glanceable card here (free desks on
 * your floor, today's occupancy). That needs `BookingService.myBookings` and
 * the occupancy roots, which arrive with the rest of the remote data layer in
 * slice 5. It is deliberately absent rather than mocked: a hardcoded "3 free
 * desks" is the single worst thing this pane could contain, because it would
 * be believed.
 */
@Component({
  selector: 'im-cmdk-preview-empty',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperKbdComponent],
  templateUrl: './cmdk-preview-empty.component.html',
  styleUrl: './cmdk-preview-empty.component.scss',
})
export class CmdkPreviewEmptyComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected readonly heading = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.empty.heading');
  });

  protected readonly subheading = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.empty.subheading');
  });

  protected readonly listLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.empty.try_a_shortcut');
  });

  protected readonly hints = computed<CmdkShortcutHint[]>(() => {
    this.locale.locale();
    const t = (key: string): string => this.transloco.translate(`shell.cmdk.empty.${key}`);
    const k = (keys: string, label: string, mod = false) => ({ keys, mod, label });
    return [
      { keys: [k('p', 'P'), k('space', 'Space')], text: t('find_a_person') },
      { keys: [k('b', 'B'), k('space', 'Space')], text: t('book_a_room') },
      { keys: [k('g', 'G'), k('space', 'Space')], text: t('go_to_a_section') },
      { keys: [k('1–9', 'One to nine', true)], text: t('quick_open') },
      { keys: [k('?', 'Question mark')], text: t('all_shortcuts') },
    ];
  });
}
